import { memo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Clock, MapPin, ArrowRight, Sparkles, Users } from "lucide-react";
import { Link } from "react-router-dom";

export const UrgencySection = memo(() => {
  const [spotsLeft, setSpotsLeft] = useState(7);
  const [timeLeft, setTimeLeft] = useState({ hours: 23, minutes: 45, seconds: 12 });

  // Simulated countdown (resets daily)
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        // Reset when countdown ends
        return { hours: 23, minutes: 59, seconds: 59 };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-12 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-red-500/10 border-y border-amber-500/20">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-amber-500/30 bg-background/80 backdrop-blur-sm shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3">
              <div className="flex items-center justify-center gap-2">
                <AlertTriangle className="w-5 h-5 animate-pulse" />
                <span className="font-bold">OFERTA POR TIEMPO LIMITADO</span>
                <AlertTriangle className="w-5 h-5 animate-pulse" />
              </div>
            </div>
            
            <CardContent className="p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                {/* Left: Urgency messaging */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-red-500 text-white">
                      <MapPin className="w-3 h-3 mr-1" />
                      Tu zona
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Plazas limitadas por ciudad
                    </span>
                  </div>
                  
                  <h3 className="text-2xl md:text-3xl font-bold">
                    Solo quedan{" "}
                    <span className="text-amber-500">{spotsLeft} plazas</span>{" "}
                    para el trial gratuito
                  </h3>
                  
                  <p className="text-muted-foreground">
                    Limitamos las plazas para garantizar atención personalizada 
                    durante tu periodo de prueba de 90 días.
                  </p>

                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-green-600">
                      <Users className="w-4 h-4" />
                      <span>12 registros hoy</span>
                    </div>
                    <div className="flex items-center gap-1 text-amber-600">
                      <Clock className="w-4 h-4" />
                      <span>Última plaza hace 8 min</span>
                    </div>
                  </div>
                </div>

                {/* Right: Countdown + CTA */}
                <div className="space-y-6">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-2">Esta oferta expira en:</p>
                    <div className="flex justify-center gap-3">
                      {[
                        { value: timeLeft.hours, label: "Horas" },
                        { value: timeLeft.minutes, label: "Min" },
                        { value: timeLeft.seconds, label: "Seg" },
                      ].map((unit, i) => (
                        <div key={i} className="text-center">
                          <div className="bg-foreground text-background rounded-lg px-4 py-3 text-2xl md:text-3xl font-mono font-bold min-w-[60px]">
                            {String(unit.value).padStart(2, "0")}
                          </div>
                          <span className="text-xs text-muted-foreground mt-1">
                            {unit.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Link to="/become-trainer" className="block">
                    <Button 
                      size="lg" 
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-lg py-6 shadow-lg"
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      Reservar Mi Plaza Gratis
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  
                  <p className="text-xs text-center text-muted-foreground">
                    ✓ Sin tarjeta de crédito &nbsp; ✓ Cancela cuando quieras &nbsp; ✓ Setup en 2 minutos
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
});

UrgencySection.displayName = "UrgencySection";
